<template>
  <CustomModal
    :title="isEditMode ? 'Update Repository' : 'Create Repository'"
    width="34vw"
    v-model:visible="internalVisible"
  >
    <div :id="`${source}_add_repository_section`" class="flex flex-col gap-6">
      <div id="name" class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Name</span>
        <a-input
          ref="nameInputRef"
          v-model:value="formData.name"
          class="input_medium"
          placeholder="Enter repository name"
          @keyup.enter="handleSubmit"
          :autofocus="true"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex flex-row items-center justify-end gap-2 w-full">
        <a-button
          class="secondary_medium"
          :disabled="saving"
          @click="handleCancel"
        >
          Cancel
        </a-button>

        <a-button
          class="primary_medium"
          :disabled="isSubmitDisabled"
          :loading="saving"
          @click="handleSubmit"
        >
          {{
            saving
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update"
                : "Create"
          }}
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, reactive, ref, watch, nextTick } from "vue";
import { message } from "ant-design-vue";
import { repositoryEvents } from "@/src/constants/codebaseConstants.js";
import { updateRepository } from "@/src/utils/codeUtils.js";
import eventBus from "@/src/utils/eventBus.js";
import CodeService from "@/src/api/CodeService.js";
import CustomModal from "@/src/components/CustomModal.vue";

const props = defineProps({
  source: { type: String, required: true },
  visible: { type: Boolean, required: true },
  space: { type: Object, default: null },
  repository: { type: Object, default: null },
  mode: { type: String, default: "create" },
});

const emit = defineEmits(["update:visible"]);

const saving = ref(false);
const internalVisible = ref(props.visible);
const nameInputRef = ref(null);

const formData = reactive({
  name: "",
});

const isEditMode = computed(() => props.mode === "edit");

const isSubmitDisabled = computed(
  () => !formData.name || formData.name.trim() === "" || saving.value,
);

const resetForm = () => {
  try {
    formData.name = "";
    saving.value = false;
  } catch (error) {
    console.error("An error occurred while resetting form: ", error);
  }
};

const handleCancel = () => {
  try {
    internalVisible.value = false;
    resetForm();
  } catch (error) {
    console.error("An error occurred while closing modal: ", error);
  }
};

const handleSubmit = async () => {
  if (isEditMode.value) {
    await toUpdateRepository();
  } else {
    await toCreateRepository();
  }
};

const toCreateRepository = async () => {
  try {
    saving.value = true;

    const payload = {
      name: formData.name,
      space_id: props.space?.id || null,
      type: "repository",
    };

    const response = await CodeService.base.repos.create(payload);
    if (response?.success) {
      message.success(`Repository ${formData.name} created successfully!`);
      internalVisible.value = false;
      resetForm();
      eventBus.emit("repository-manipulated", repositoryEvents.CREATED);
    }
  } catch (error) {
    console.error("An error occurred while creating repository: ", error);
    message.error(error?.response?.data?.error || "Failed to create repository");
  } finally {
    saving.value = false;
  }
};

const toUpdateRepository = async () => {
  try {
    saving.value = true;
    const response = await updateRepository(props.repository?.id, {
      name: formData.name,
    });
    if (response?.success) {
      internalVisible.value = false;
      resetForm();
    }
  } catch (error) {
    console.error("An error occurred while updating repository: ", error);
    message.error(error?.response?.data?.error || "Failed to update repository");
  } finally {
    saving.value = false;
  }
};

watch(
  () => props.visible,
  async (newVal) => {
    internalVisible.value = newVal;
    if (newVal) {
      await nextTick();
      nameInputRef.value?.focus();
    }
  },
  { immediate: true },
);

watch(
  () => internalVisible.value,
  (newVal) => {
    emit("update:visible", newVal);
    if (!newVal) {
      resetForm();
    }
  },
);

watch(
  () => [props.repository, props.visible, props.mode],
  ([repository, visible, mode]) => {
    if (visible && mode === "edit" && repository) {
      formData.name = repository.name || "";
    }
  },
  { immediate: true },
);
</script>
